/**
 * Dashboard widgets (phase-1-scope §1.1). One component per widget code declared in a module manifest; the map
 * at the bottom is what `apps/api/test/consistency.test.ts` checks against the manifests.
 *
 * Rules every widget follows:
 *  - it fetches its own module's summary endpoint (small, cached, already filtered by record visibility), so one
 *    slow or failing module never blanks the page — MRLCO's executive page depended on one 34-query endpoint;
 *  - every figure is a link to the filtered list behind it (drill-through), never a dead number;
 *  - it renders nothing module-specific when there is no data, only the shared empty line.
 */
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import type { BeneficiarySummary, DirectorySummary, LeaveSummary, NotificationList, PeopleSummary, ProgramsOverview, StakeholderSummary, StrategySummary, TaskSummary } from '@mnc/contracts';
import { api } from '../../lib/api';
import { referenceRoute } from '../../lib/references';
import { pick } from '../../i18n';
import { cn } from '../../components/ui';
import { ProgressRing, StatusDistribution, useLevelLabel, useStrategyConfig } from '../strategy/shared';
import { fmtNumber } from '../stakeholders/shared';
import type { ContractsFollowup, PerformanceSummary, RegistryProposalList, SsoEntityList } from '@mnc/contracts';
import { Points } from '../performance/shared';
import { CheckCard } from '../attendance/shared';
import { EntityRow, useOpenEntity } from '../people/sso';
import { Money, ObligationLine } from '../service-contracts/shared';

const STALE = 30_000;

export interface WidgetState {
  loading: boolean;
  error: boolean;
  retry: () => void;
  /** Where the widget's title leads. */
  to: string;
  children: ReactNode;
}

/** A big number that is a link. */
function Stat({ value, label, to, search, tone = 'ok' }: { value: string; label: string; to: string; search?: Record<string, unknown>; tone?: 'ok' | 'warn' | 'danger' }) {
  return (
    <Link to={to} search={search as never} className="block rounded-lg border border-neutral-200 p-2.5 transition hover:border-brand-500 dark:border-neutral-700">
      <div className={cn('text-2xl font-semibold tabular-nums', tone === 'warn' && 'text-amber-600', tone === 'danger' && 'text-red-600')}>{value}</div>
      <div className="text-xs text-neutral-600 dark:text-neutral-300">{label}</div>
    </Link>
  );
}

/** A labelled bar row that is a link. */
function BarRow({ label, count, max, to, search }: { label: string; count: number; max: number; to: string; search?: Record<string, unknown> }) {
  const { i18n } = useTranslation();
  return (
    <Link to={to} search={search as never} className="group block py-1">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="truncate group-hover:underline" dir="auto">{label}</span>
        <span className="tabular-nums text-neutral-600 dark:text-neutral-300">{fmtNumber(count, i18n.language)}</span>
      </div>
      <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
        <div className="h-full rounded-full bg-brand-500" style={{ width: `${max > 0 ? Math.max(2, (count / max) * 100) : 0}%` }} />
      </div>
    </Link>
  );
}

const Empty = ({ children }: { children: ReactNode }) => <p className="py-4 text-center text-sm text-neutral-500">{children}</p>;

function useSummary<T>(key: string[], url: string) {
  return useQuery({ queryKey: key, queryFn: () => api.get<T>(url), staleTime: STALE });
}

// ---------------------------------------------------------------------------------------------------------------

function MyOpenTasks(): WidgetState {
  const { t, i18n } = useTranslation();
  const q = useSummary<TaskSummary>(['tasks', 'summary'], '/api/tasks/summary');
  const d = q.data;
  const max = Math.max(1, ...(d?.my_open_by_urgency ?? []).map((u) => u.count));
  return {
    loading: q.isLoading,
    error: q.isError,
    retry: () => void q.refetch(),
    to: '/tasks',
    children: d && (
      <div>
        <div className="grid grid-cols-3 gap-2">
          <Stat value={fmtNumber(d.my_open, i18n.language)} label={t('dashboard.tasks.open')} to="/tasks" search={{ mine: '1' }} />
          <Stat value={fmtNumber(d.my_overdue, i18n.language)} label={t('dashboard.tasks.overdue')} to="/tasks" search={{ mine: '1', view: 'overdue' }} tone={d.my_overdue > 0 ? 'danger' : 'ok'} />
          <Stat value={fmtNumber(d.my_due_this_week, i18n.language)} label={t('dashboard.tasks.dueThisWeek')} to="/tasks" search={{ mine: '1', sort: 'due_date' }} tone={d.my_due_this_week > 0 ? 'warn' : 'ok'} />
        </div>
        {d.my_open_by_urgency.length > 0 && (
          <div className="mt-3">
            <div className="label">{t('dashboard.tasks.byUrgency')}</div>
            {d.my_open_by_urgency.map((u) => (
              <BarRow key={u.code ?? 'none'} label={u.code ? pick(i18n.language, u.name_en ?? u.code, u.name_ar ?? u.code) : t('dashboard.tasks.noUrgency')} count={u.count} max={max} to="/tasks" search={u.code ? { mine: '1', urgency: u.code } : { mine: '1' }} />
            ))}
          </div>
        )}
        {d.my_open === 0 && <Empty>{t('dashboard.tasks.none')}</Empty>}
      </div>
    ),
  };
}

function TeamOverdue(): WidgetState {
  const { t, i18n } = useTranslation();
  const q = useSummary<TaskSummary>(['tasks', 'summary'], '/api/tasks/summary');
  const rows = q.data?.team_overdue ?? [];
  const max = Math.max(1, ...rows.map((r) => r.count));
  return {
    loading: q.isLoading,
    error: q.isError,
    retry: () => void q.refetch(),
    to: '/tasks',
    children: q.data && (rows.length === 0 ? <Empty>{t('dashboard.tasks.noneOverdue')}</Empty> : rows.map((r) => <BarRow key={r.user.id} label={pick(i18n.language, r.user.display_name_en, r.user.display_name_ar)} count={r.count} max={max} to="/tasks" search={{ view: 'overdue', assignees: r.user.id }} />)),
  };
}

function StrategyProgress(): WidgetState {
  const { t, i18n } = useTranslation();
  const q = useSummary<StrategySummary>(['strategy', 'summary'], '/api/strategy/summary');
  const levelLabel = useLevelLabel(useStrategyConfig().data);
  const d = q.data;
  return {
    loading: q.isLoading,
    error: q.isError,
    retry: () => void q.refetch(),
    to: '/strategy',
    children:
      d &&
      (d.by_level.every((l) => l.total === 0) ? (
        <Empty>{t('strategy.noNodes')}</Empty>
      ) : (
        <div className="grid gap-4 md:grid-cols-[auto_minmax(0,1fr)]">
          <Link to="/strategy" search={{ tab: 'kpis' } as never} className="flex items-center gap-3 rounded-lg border border-neutral-200 p-3 transition hover:border-brand-500 dark:border-neutral-700">
            <ProgressRing value={d.kpi_progress} size={72} stroke={7} />
            <div>
              <div className="text-sm font-medium">{t('dashboard.strategy.kpiProgress')}</div>
              <div className="text-xs text-neutral-500">{t('dashboard.strategy.kpiCount', { n: d.kpi_count })}</div>
              <div className="mt-1 text-xs">
                <span className={cn(d.open_challenges > 0 && 'text-amber-600')}>{t('dashboard.strategy.challenges', { n: d.open_challenges })}</span>
                {' · '}
                <span className={cn(d.tasks_overdue > 0 && 'text-red-600')}>{t('dashboard.strategy.tasksOverdue', { n: d.tasks_overdue })}</span>
              </div>
            </div>
          </Link>
          <div className="space-y-2">
            {d.by_level
              .filter((l) => l.total > 0)
              .map((l) => (
                <Link key={l.level} to="/strategy" search={{ tab: 'overview', focus: l.level } as never} className="group block">
                  <div className="flex items-center justify-between text-sm">
                    <span className="group-hover:underline">{levelLabel(l.level, true)}</span>
                    <span className="tabular-nums text-neutral-600 dark:text-neutral-300">{fmtNumber(l.total, i18n.language)}</span>
                  </div>
                  <StatusDistribution counts={l.by_status} />
                </Link>
              ))}
          </div>
        </div>
      )),
  };
}

function StakeholderPipeline(): WidgetState {
  const { t, i18n } = useTranslation();
  const q = useSummary<StakeholderSummary>(['stakeholders', 'summary'], '/api/stakeholders/summary');
  const d = q.data;
  return {
    loading: q.isLoading,
    error: q.isError,
    retry: () => void q.refetch(),
    to: '/stakeholders',
    children:
      d &&
      (d.total === 0 ? (
        <Empty>{t('dashboard.stakeholders.none')}</Empty>
      ) : (
        <div>
          <div className="flex flex-wrap items-stretch gap-1">
            {d.by_engagement.map((step, i) => (
              <Link key={step.code} to="/stakeholders" search={{ engagement: step.code } as never} className="flex min-w-20 flex-1 items-center gap-1 rounded-lg border border-neutral-200 px-2 py-1.5 text-xs transition hover:border-brand-500 dark:border-neutral-700">
                {i > 0 && <ChevronRight size={12} className="shrink-0 text-neutral-400 rtl:rotate-180" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-neutral-500" dir="auto">{pick(i18n.language, step.name_en, step.name_ar)}</span>
                  <span className="text-lg font-semibold tabular-nums">{fmtNumber(step.count, i18n.language)}</span>
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-2 text-xs text-neutral-500">{t('dashboard.stakeholders.total', { n: d.total })}</div>
        </div>
      )),
  };
}

function ProgramsGlance(): WidgetState {
  const { t, i18n } = useTranslation();
  const q = useSummary<ProgramsOverview>(['stakeholders', 'programs-overview'], '/api/stakeholders/programs/overview');
  const d = q.data;
  return {
    loading: q.isLoading,
    error: q.isError,
    retry: () => void q.refetch(),
    to: '/stakeholders/programs',
    children:
      d &&
      (d.programs_total === 0 ? (
        <Empty>{t('dashboard.programs.none')}</Empty>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Stat value={fmtNumber(d.programs_total, i18n.language)} label={t('dashboard.programs.programs')} to="/stakeholders/programs" />
          <Stat value={fmtNumber(d.enrolled_beneficiaries, i18n.language)} label={t('stakeholders.stats.beneficiariesFromPrograms')} to="/beneficiaries" search={{ enrolled: '1' }} />
          <Stat value={fmtNumber(d.stakeholders_with_programs, i18n.language)} label={t('stakeholders.stats.stakeholdersWithPrograms')} to="/stakeholders" search={{ has_programs: '1' }} />
          {d.total_value.combined !== null && <Stat value={fmtNumber(d.total_value.combined, i18n.language)} label={t('stakeholders.stats.totalEconomicValue')} to="/stakeholders/programs" />}
        </div>
      )),
  };
}

function BeneficiariesByType(): WidgetState {
  const { t, i18n } = useTranslation();
  const q = useSummary<BeneficiarySummary>(['beneficiaries', 'summary'], '/api/beneficiaries/summary');
  const d = q.data;
  const rows = (d?.by_type ?? []).filter((r) => r.count > 0).slice(0, 6);
  const max = Math.max(1, ...rows.map((r) => r.count));
  return {
    loading: q.isLoading,
    error: q.isError,
    retry: () => void q.refetch(),
    to: '/beneficiaries',
    children:
      d &&
      (d.total === 0 ? (
        <Empty>{t('dashboard.beneficiaries.none')}</Empty>
      ) : (
        <div>
          <div className="mb-2 grid grid-cols-3 gap-2">
            <Stat value={fmtNumber(d.organisations, i18n.language)} label={t('beneficiaries.kindsPlural.organisation')} to="/beneficiaries" search={{ kind: 'organisation' }} />
            <Stat value={fmtNumber(d.individuals, i18n.language)} label={t('beneficiaries.kindsPlural.individual')} to="/beneficiaries" search={{ kind: 'individual' }} />
            <Stat value={fmtNumber(d.enrolled, i18n.language)} label={t('dashboard.beneficiaries.enrolled')} to="/beneficiaries" search={{ enrolled: '1' }} />
          </div>
          {rows.map((r) => <BarRow key={r.code} label={pick(i18n.language, r.name_en, r.name_ar)} count={r.count} max={max} to="/beneficiaries" search={{ type: r.code }} />)}
        </div>
      )),
  };
}

function RecentNotifications(): WidgetState {
  const { t, i18n } = useTranslation();
  const q = useQuery({ queryKey: ['notifications', 'recent'], queryFn: () => api.get<NotificationList>('/api/notifications?page_size=6'), staleTime: STALE });
  const items = q.data?.items ?? [];
  return {
    loading: q.isLoading,
    error: q.isError,
    retry: () => void q.refetch(),
    to: '/notifications',
    children:
      q.data &&
      (items.length === 0 ? (
        <Empty>{t('dashboard.notifications.none')}</Empty>
      ) : (
        <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {items.map((n) => (
            <li key={n.id}>
              <Link to={referenceRoute(n.reference_type, n.reference_id) ?? '/notifications'} className="flex items-start gap-2 py-1.5 text-sm hover:underline">
                <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', n.is_read ? 'bg-neutral-300 dark:bg-neutral-700' : 'bg-brand-500')} />
                <span className="min-w-0 flex-1">
                  <span className={cn('block truncate', !n.is_read && 'font-medium')} dir="auto">{pick(i18n.language, n.title_en, n.title_ar)}</span>
                  <span className="text-xs text-neutral-500">{new Date(n.created_at).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )),
  };
}

function DirectoryHealth(): WidgetState {
  const { t, i18n } = useTranslation();
  const q = useSummary<DirectorySummary>(['entities', 'summary'], '/api/entities/summary');
  const d = q.data;
  return {
    loading: q.isLoading,
    error: q.isError,
    retry: () => void q.refetch(),
    to: '/entities',
    children:
      d &&
      (d.total === 0 ? (
        <Empty>{t('directory.none')}</Empty>
      ) : (
        <div>
          <div className="mb-2 grid grid-cols-3 gap-2">
            <Stat value={fmtNumber(d.by_status.active ?? 0, i18n.language)} label={t('directory.statuses.active')} to="/entities" search={{ status: 'active' }} />
            <Stat value={fmtNumber(d.healthy, i18n.language)} label={t('directory.healthy')} to="/entities" search={{ status: 'active' }} />
            <Stat value={fmtNumber(d.attention.length, i18n.language)} label={t('directory.attention')} to="/entities" search={{ attention: '1' }} tone={d.attention.length > 0 ? 'danger' : 'ok'} />
          </div>
          {d.attention.slice(0, 5).map((e) => (
            <Link key={e.id} to="/entities" search={{ q: e.slug } as never} className="flex items-center justify-between gap-2 py-1 text-sm hover:underline">
              <span className="truncate" dir="auto">{pick(i18n.language, e.name_en, e.name_ar)}</span>
              <span className="shrink-0 text-xs text-amber-600">{e.drift.map((x) => t(`directory.drift.${x}`)).join(' · ')}</span>
            </Link>
          ))}
        </div>
      )),
  };
}

function AllocationsEnding(): WidgetState {
  const { t, i18n } = useTranslation();
  const q = useSummary<PeopleSummary>(['people', 'summary'], '/api/people/summary');
  const d = q.data;
  return {
    loading: q.isLoading,
    error: q.isError,
    retry: () => void q.refetch(),
    to: '/people/allocations',
    children:
      d &&
      (d.ending_soon.length === 0 ? (
        <Empty>{t('people.noneEnding')}</Empty>
      ) : (
        <div>
          {d.ending_soon.slice(0, 6).map((a) => (
            <Link key={a.id} to="/people/$id" params={{ id: a.employee.id } as never} className="flex items-center justify-between gap-2 py-1 text-sm hover:underline">
              <span className="min-w-0 truncate" dir="auto">{pick(i18n.language, a.employee.display_name_en, a.employee.display_name_ar)} · <span className="text-neutral-500">{pick(i18n.language, a.entity.name_en, a.entity.name_ar)}</span></span>
              <span className={cn('shrink-0 text-xs tabular-nums', (a.days_to_end ?? 99) <= 7 ? 'font-semibold text-red-600' : 'text-amber-600')}>{t('people.endsIn', { count: a.days_to_end ?? 0 })}</span>
            </Link>
          ))}
          <Link to="/people/allocations" search={{ ending: '1' } as never} className="mt-1 inline-flex items-center gap-1 text-xs text-brand-700 hover:underline dark:text-brand-100">{t('people.endingSoon')}<ChevronRight size={12} className="rtl:rotate-180" /></Link>
        </div>
      )),
  };
}

function PeopleUtilisation(): WidgetState {
  const { t, i18n } = useTranslation();
  const q = useSummary<PeopleSummary>(['people', 'summary'], '/api/people/summary');
  const d = q.data;
  // A sum of percentages says nothing; a hundred of them is one full-time equivalent.
  const max = Math.max(1, ...(d?.by_entity ?? []).map((e) => e.percent / 100));
  return {
    loading: q.isLoading,
    error: q.isError,
    retry: () => void q.refetch(),
    to: '/people/allocations',
    children:
      d &&
      (d.headcount === 0 ? (
        <Empty>{t('people.none')}</Empty>
      ) : (
        <div>
          <div className="mb-2 grid grid-cols-3 gap-2">
            <Stat value={`${fmtNumber(d.average_utilisation, i18n.language)}%`} label={t('people.averageUtilisation')} to="/people/allocations" />
            <Stat value={fmtNumber(d.unallocated, i18n.language)} label={t('people.unallocated')} to="/people" search={{ free: '1' }} tone={d.unallocated > 0 ? 'warn' : 'ok'} />
            <Stat value={fmtNumber(d.over_allocated.length, i18n.language)} label={t('people.overAllocated')} to="/people" search={{ over: '1' }} tone={d.over_allocated.length > 0 ? 'danger' : 'ok'} />
          </div>
          {d.by_entity.length > 0 && <div className="text-xs text-neutral-500">{t('people.fteByEntity')}</div>}
          {d.by_entity.slice(0, 5).map((e) => <BarRow key={e.id} label={`${pick(i18n.language, e.name_en, e.name_ar)} · ${t('people.peopleCount', { count: e.people })}`} count={e.percent / 100} max={max} to="/people/allocations" search={{ entity: e.id, view: 'entity' }} />)}
        </div>
      )),
  };
}

function MyLeave(): WidgetState {
  const { t, i18n } = useTranslation();
  const q = useSummary<LeaveSummary>(['leave', 'summary'], '/api/leave/summary');
  const d = q.data;
  const date = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' });
  return {
    loading: q.isLoading,
    error: q.isError,
    retry: () => void q.refetch(),
    to: '/leave',
    children:
      d &&
      (!d.is_employee || d.balance_days === null ? (
        <Empty>{t('leave.notEmployee')}</Empty>
      ) : (
        <div>
          <Stat value={fmtNumber(d.balance_days, i18n.language)} label={t('leave.availableDays')} to="/leave" tone={d.balance_days < 0 ? 'danger' : 'ok'} />
          {d.next_leave && <Link to="/leave/requests/$id" params={{ id: d.next_leave.id } as never} className="mt-2 block text-sm hover:underline"><span className="text-neutral-500">{t('leave.nextLeave')}: </span>{pick(i18n.language, d.next_leave.type_en, d.next_leave.type_ar)} · {date(d.next_leave.start_date)}</Link>}
          {d.my_pending > 0 && <div className="mt-1 text-xs text-amber-600">{t('leave.myPending', { count: d.my_pending })}</div>}
        </div>
      )),
  };
}

function LeaveApprovals(): WidgetState {
  const { t, i18n } = useTranslation();
  const q = useSummary<LeaveSummary>(['leave', 'summary'], '/api/leave/summary');
  const d = q.data;
  const date = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return {
    loading: q.isLoading,
    error: q.isError,
    retry: () => void q.refetch(),
    to: '/leave',
    children:
      d &&
      (d.inbox_total === 0 ? (
        <Empty>{t('leave.nothingToDecide')}</Empty>
      ) : (
        <div>
          {d.inbox.map((r) => (
            <Link key={r.id} to="/leave/requests/$id" params={{ id: r.id } as never} search={{ tab: 'approvals' } as never} className="flex items-center justify-between gap-2 py-1 text-sm hover:underline">
              <span className="min-w-0 truncate" dir="auto">{pick(i18n.language, r.employee.display_name_en, r.employee.display_name_ar)} · <span className="text-neutral-500">{pick(i18n.language, r.leave_type.name_en, r.leave_type.name_ar)}</span></span>
              <span className="shrink-0 text-xs tabular-nums text-neutral-500">{date(r.start_date)} · {fmtNumber(r.days, i18n.language)}</span>
            </Link>
          ))}
          {d.inbox_total > d.inbox.length && <Link to="/leave" search={{ tab: 'approvals' } as never} className="mt-1 inline-flex items-center gap-1 text-xs text-brand-700 hover:underline dark:text-brand-100">{t('leave.moreToDecide', { count: d.inbox_total - d.inbox.length })}<ChevronRight size={12} className="rtl:rotate-180" /></Link>}
        </div>
      )),
  };
}

/** The entities I may open now (consultant single sign-on): one click, no second password. */
function MyEntities(): WidgetState {
  const { t } = useTranslation();
  const q = useSummary<SsoEntityList>(['sso', 'entities'], '/api/sso/entities');
  const open = useOpenEntity();
  return {
    loading: q.isLoading,
    error: q.isError,
    retry: () => void q.refetch(),
    to: '/sso/start',
    children: q.data && (q.data.items.length === 0 ? <Empty>{t('sso.none')}</Empty> : <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">{q.data.items.map((e) => <EntityRow key={e.allocation_id} e={e} busy={open.isPending} onOpen={(slug) => open.mutate(slug)} />)}</ul>),
  };
}

/** My latest shared review, and what waits for me (an acknowledgement, a self-assessment). */
function PerformanceMine(): WidgetState {
  const { t, i18n } = useTranslation();
  const q = useSummary<PerformanceSummary>(['performance', 'summary'], '/api/performance/summary');
  const d = q.data;
  return {
    loading: q.isLoading,
    error: q.isError,
    retry: () => void q.refetch(),
    to: '/performance',
    children:
      d &&
      (!d.latest && d.self_assessments_open === 0 ? (
        <Empty>{t('performance.noReviews')}</Empty>
      ) : (
        <div className="grid gap-2">
          {d.latest && (
            <Link to="/performance" search={{ review: d.latest.review_id } as never} className="block rounded-lg border border-neutral-200 p-2.5 transition hover:border-brand-500 dark:border-neutral-700">
              <div className="text-xl font-semibold"><Points points={d.latest.total_points} percent={d.latest.percent} /></div>
              <div className="text-xs text-neutral-600 dark:text-neutral-300" dir="auto">{pick(i18n.language, d.latest.cycle_name_en, d.latest.cycle_name_ar)}</div>
            </Link>
          )}
          {d.to_acknowledge > 0 && <Link to="/performance" className="text-sm text-amber-700 hover:underline dark:text-amber-300">{t('performance.toAcknowledge', { count: d.to_acknowledge })}</Link>}
          {d.self_assessments_open > 0 && <Link to="/performance" className="text-sm text-amber-700 hover:underline dark:text-amber-300">{t('performance.selfOpenCount', { count: d.self_assessments_open })}</Link>}
        </div>
      )),
  };
}

/** The check-in card itself: the home page is where a day starts (MRLCO had it there too). It loads and fails on its own. */
function AttendanceToday(): WidgetState {
  return { loading: false, error: false, retry: () => undefined, to: '/attendance', children: <CheckCard compact /> };
}

/** Widget code (as declared in a module manifest) → component hook. Keep in step with the manifests. */
/**
 * Contract follow-up: what is due in the next 30 days (mine, or everyone's for a contract manager) and what the
 * clients owe past its due date. The brief asked for this; MRLCO had no follow-up at all.
 */
function ContractsFollowups(): WidgetState {
  const { t } = useTranslation();
  const q = useSummary<ContractsFollowup>(['contracts', 'followups'], '/api/contracts/followups');
  const d = q.data;
  return {
    loading: q.isLoading,
    error: q.isError,
    retry: () => void q.refetch(),
    to: '/contracts',
    children:
      d &&
      (!d.obligations.length && !d.overdue.length ? (
        <Empty>{t('contracts.nothingDue')}</Empty>
      ) : (
        <div className="grid gap-2">
          {d.overdue.map((o) => (
            <Link key={o.currency} to="/contracts" search={{ view: 'receivables' } as never} className="flex items-center justify-between gap-2 rounded-lg border border-amber-300 p-2 text-sm hover:border-amber-500 dark:border-amber-800">
              <span className="text-amber-700 dark:text-amber-300">{t('contracts.overdueInvoices', { count: o.invoices })}</span>
              <Money minor={o.amount_minor} currency={o.currency} className="font-semibold" />
            </Link>
          ))}
          <ul className="grid gap-1">
            {d.obligations.slice(0, 5).map((o) => (
              <li key={o.id} className="flex items-center justify-between gap-2">
                <ObligationLine o={o} showContract />
                <ChevronRight size={14} className="shrink-0 text-neutral-400 rtl:rotate-180" />
              </li>
            ))}
          </ul>
          {d.obligations.length > 5 && <Link to="/contracts" search={{ view: 'followups' } as never} className="text-xs text-brand-700 hover:underline dark:text-brand-300">{t('contracts.seeAllFollowups', { count: d.obligations.length })}</Link>}
        </div>
      )),
  };
}

/** Registry proposals waiting on central (Phase 3 step 4): how many, and the newest five. */
function RegistryProposals(): WidgetState {
  const { t, i18n } = useTranslation();
  const q = useSummary<RegistryProposalList>(['registry', 'proposals', 'widget'], '/api/registry/proposals?status=pending&page_size=5');
  const d = q.data;
  return {
    loading: q.isLoading,
    error: q.isError,
    retry: () => void q.refetch(),
    to: '/registry/proposals',
    children:
      d &&
      (d.meta.total === 0 ? (
        <Empty>{t('registry.proposals.none')}</Empty>
      ) : (
        <div>
          <div className="mb-2"><Stat value={fmtNumber(d.meta.total, i18n.language)} label={t('registry.proposals.statuses.pending')} to="/registry/proposals" tone="warn" /></div>
          <ul className="grid gap-1 text-sm">
            {d.items.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-2">
                <span className="truncate" dir="auto">{p.master ? pick(i18n.language, p.master.name_en, p.master.name_ar) : pick(i18n.language, p.fields.name_en ?? '', p.fields.name_ar ?? '')}</span>
                <span className="shrink-0 text-xs text-neutral-500">{t(`registry.proposals.types.${p.type}`)} · {pick(i18n.language, p.entity.name_en, p.entity.name_ar)}</span>
              </li>
            ))}
          </ul>
        </div>
      )),
  };
}

export const WIDGETS: Record<string, () => WidgetState> = {
  'tasks.my_open': MyOpenTasks,
  'tasks.team_overdue': TeamOverdue,
  'strategy.progress': StrategyProgress,
  'stakeholders.pipeline': StakeholderPipeline,
  'stakeholders.programs': ProgramsGlance,
  'beneficiaries.by_type': BeneficiariesByType,
  'notifications.recent': RecentNotifications,
  'directory.health': DirectoryHealth,
  'people.allocations_ending': AllocationsEnding,
  'people.utilisation': PeopleUtilisation,
  'leave.my_balance': MyLeave,
  'leave.approvals': LeaveApprovals,
  'attendance.today': AttendanceToday,
  'performance.mine': PerformanceMine,
  'people.my_entities': MyEntities,
  'contracts.followups': ContractsFollowups,
  'registry.proposals': RegistryProposals,
};

export function WidgetError({ retry }: { retry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-2 py-5 text-center text-sm text-neutral-500">
      <AlertTriangle size={18} className="text-amber-500" />
      {t('dashboard.widgetError')}
      <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={retry}>{t('app.refresh')}</button>
    </div>
  );
}
